import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { ApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';
import LoginModal from '../components/LoginModal';
import AuroraBackground from '../components/AuroraBackground';

const SessionsListPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { instagramAccountsTriggered, instagramAccountsOwning, triggeredAccountsWithContext, accountsLoading, contextLoading, refreshAccounts } = useUserData();

    // Create session form state
    const [accountInput, setAccountInput] = useState('');
    const [hashtagInput, setHashtagInput] = useState('');
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    // Login modal state for existing account auth
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [pendingAccountUrl, setPendingAccountUrl] = useState<string | null>(null);
    const [pendingHashtags, setPendingHashtags] = useState<string[] | undefined>(undefined);

    // Compute stats from triggered accounts context
    const stats = useMemo(() => {
        let commentsPosted = 0, commentsGenerated = 0, pendingInQueue = 0;
        for (const account of triggeredAccountsWithContext) {
            commentsPosted += account.postedUrls.length;
            pendingInQueue += account.pendingCount;
            for (const session of account.recentSessions) {
                commentsGenerated += session.commentCount;
            }
        }
        return { commentsPosted, commentsGenerated, pendingInQueue };
    }, [triggeredAccountsWithContext]);

    // Helper to lookup hints from cached context
    const lookupHintsFromCachedContext = useCallback((accountUrl: string): { isOwner?: boolean; hasSessions?: boolean } => {
        // Extract username from URL for matching
        const extractUsername = (url: string): string => {
            try {
                const urlObj = new URL(url);
                const pathParts = urlObj.pathname.split('/').filter(p => p);
                return pathParts[0]?.toLowerCase() || '';
            } catch {
                return url.replace('@', '').toLowerCase();
            }
        };

        const targetUsername = extractUsername(accountUrl);

        // Check owning accounts first (user owns this account)
        const owningAccount = instagramAccountsOwning.find(acc => {
            const accUsername = extractUsername(acc.accountUrl);
            return accUsername === targetUsername || acc.accountId.toLowerCase() === targetUsername;
        });
        if (owningAccount) {
            console.log('[Hints] Found in owning accounts:', { isOwner: true, hasSessions: owningAccount.sessionCount > 0 });
            return { isOwner: true, hasSessions: owningAccount.sessionCount > 0 };
        }

        // Check triggered accounts (user has created sessions but may not own)
        const triggeredAccount = instagramAccountsTriggered.find(acc => {
            const accUsername = extractUsername(acc.accountUrl);
            return accUsername === targetUsername || acc.accountId.toLowerCase() === targetUsername;
        });
        if (triggeredAccount) {
            console.log('[Hints] Found in triggered accounts:', { isOwner: false, hasSessions: triggeredAccount.sessionCount > 0 });
            return { isOwner: false, hasSessions: triggeredAccount.sessionCount > 0 };
        }

        // Account not in cached context - no hints available
        console.log('[Hints] Account not found in cached context');
        return {};
    }, [instagramAccountsOwning, instagramAccountsTriggered]);

    const handleCreateSession = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreateError(null);

        if (!accountInput.trim()) {
            setCreateError('Please enter your Instagram username or URL');
            return;
        }

        let accountUrl = accountInput.trim();
        if (!accountUrl.startsWith('http')) {
            accountUrl = `https://instagram.com/${accountUrl.replace('@', '')}`;
        }

        const urlPattern = /^https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9_.]+\/?$/;
        if (!urlPattern.test(accountUrl)) {
            setCreateError('Please enter a valid Instagram username or URL');
            return;
        }

        let hashtags: string[] | undefined;
        if (hashtagInput.trim()) {
            hashtags = hashtagInput
                .split(/[,\s]+/)
                .map(h => h.replace(/^#/, '').trim())
                .filter(h => h.length > 0);
        }

        // Require login before creating session
        if (!user) {
            setCreateError('Please sign in to create a session');
            return;
        }

        // Pre-flight credit check
        const balance = user.creditBalance ?? 0;
        if (balance <= 0) {
            navigate('/plans');
            return;
        }

        setCreateLoading(true);

        try {
            // Lookup hints from cached context to optimize backend Firestore reads
            const hints = lookupHintsFromCachedContext(accountUrl);
            const response = await api.createSession(accountUrl, hashtags, hints.isOwner, hints.hasSessions);
            refreshAccounts(); // fire-and-forget — update sidebar context in background
            navigate(`/session/${response.id}`);
        } catch (err: any) {
            // Check if this is an auth required error for existing account
            // Use duck typing instead of instanceof to handle module boundary issues
            const isAuthRequired =
                (err instanceof ApiError && err.code === 'auth_required') ||
                (err?.code === 'auth_required') ||
                (err?.status === 401) ||
                (err?.message?.includes('Authentication required'));

            const isNoCredits =
                (err instanceof ApiError && err.code === 'subscription_required') ||
                (err?.code === 'subscription_required') ||
                (err?.status === 402);

            console.log('Session creation error:', err, 'isAuthRequired:', isAuthRequired, 'isNoCredits:', isNoCredits);

            if (isAuthRequired) {
                // Store pending session info and show login modal
                setPendingAccountUrl(accountUrl);
                setPendingHashtags(hashtags);
                setShowLoginModal(true);
                setCreateError(null);
            } else if (isNoCredits) {
                navigate(`/plans?redirect=${encodeURIComponent(location.pathname)}`);
            } else {
                setCreateError(err instanceof Error ? err.message : 'Failed to create session');
            }
        } finally {
            setCreateLoading(false);
        }
    };

    // Handle successful login - retry session creation
    const handleLoginSuccess = async () => {
        setShowLoginModal(false);
        if (pendingAccountUrl) {
            setCreateLoading(true);
            setCreateError(null);
            try {
                // After login, user context may have changed - lookup hints again
                const hints = lookupHintsFromCachedContext(pendingAccountUrl);
                const response = await api.createSession(pendingAccountUrl, pendingHashtags, hints.isOwner, hints.hasSessions);
                refreshAccounts(); // fire-and-forget — update sidebar context in background
                navigate(`/session/${response.id}`);
            } catch (err: any) {
                const isNoCredits =
                    (err instanceof ApiError && err.code === 'subscription_required') ||
                    (err?.code === 'subscription_required') ||
                    (err?.status === 402);
                if (isNoCredits) {
                    navigate('/plans');
                } else {
                    setCreateError(err instanceof Error ? err.message : 'Failed to create session');
                }
            } finally {
                setCreateLoading(false);
                setPendingAccountUrl(null);
                setPendingHashtags(undefined);
            }
        }
    };

    // Extract username from URL for display
    const extractUsernameFromUrl = (url: string) => {
        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/').filter(p => p);
            return pathParts[0] || url;
        } catch {
            return url.replace(/^@/, '');
        }
    };


    return (
        <>
            <AuroraBackground />
            <div style={{
                minHeight: '100vh',
                paddingTop: '100px',
                paddingBottom: '60px',
            }}>
                <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
                    {/* Header */}
                    <div style={{ marginBottom: '40px' }}>
                        <h1 style={{
                            fontSize: '32px',
                            fontWeight: 600,
                            color: '#000000',
                            marginBottom: '8px',
                        }}>
                            Welcome back{user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}
                        </h1>
                        <p style={{
                            color: '#666666',
                            fontSize: '16px',
                        }}>
                            Your comment campaign dashboard
                        </p>
                    </div>

                    {/* Create Session Card - hidden for users with no credits who already have sessions */}
                    {((user?.creditBalance ?? 0) > 0 || triggeredAccountsWithContext.length === 0) ? (
                    <div style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: '12px',
                        padding: '32px',
                        marginBottom: '48px',
                    }}>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: 600,
                            color: '#000000',
                            marginBottom: '20px',
                        }}>
                            Generate New Comments
                        </h2>
                        <form onSubmit={handleCreateSession} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <input
                                    type="text"
                                    value={accountInput}
                                    onChange={(e) => setAccountInput(e.target.value)}
                                    placeholder="Instagram username or URL"
                                    disabled={createLoading}
                                    style={{
                                        flex: '1 1 300px',
                                        padding: '14px 16px',
                                        background: '#FFFFFF',
                                        border: '1px solid rgba(0, 0, 0, 0.15)',
                                        borderRadius: '6px',
                                        color: '#000000',
                                        fontSize: '15px',
                                        outline: 'none',
                                    }}
                                />
                                <button
                                    type="submit"
                                    disabled={createLoading}
                                    style={{
                                        padding: '14px 28px',
                                        background: createLoading ? '#999999' : '#7C3AED',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        cursor: createLoading ? 'not-allowed' : 'pointer',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {createLoading ? 'Creating...' : 'Generate Comments'}
                                </button>
                            </div>
                            <input
                                type="text"
                                value={hashtagInput}
                                onChange={(e) => setHashtagInput(e.target.value)}
                                placeholder="Optional: hashtags to explore (e.g. fitness, yoga)"
                                disabled={createLoading}
                                style={{
                                    padding: '12px 16px',
                                    background: '#FFFFFF',
                                    border: '1px solid rgba(0, 0, 0, 0.1)',
                                    borderRadius: '6px',
                                    color: '#000000',
                                    fontSize: '14px',
                                    outline: 'none',
                                }}
                            />
                            {createError && (
                                <div style={{
                                    color: '#EF4444',
                                    fontSize: '14px',
                                    padding: '12px 16px',
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(239, 68, 68, 0.2)',
                                }}>
                                    {createError}
                                </div>
                            )}
                        </form>
                    </div>
                    ) : (
                    <div style={{
                        background: 'rgba(124, 58, 237, 0.05)',
                        border: '1px solid rgba(124, 58, 237, 0.2)',
                        borderRadius: '12px',
                        padding: '24px 32px',
                        marginBottom: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '16px',
                    }}>
                        <div>
                            <p style={{ color: '#000000', fontSize: '15px', fontWeight: 500, margin: '0 0 4px' }}>
                                Want to create more sessions?
                            </p>
                            <p style={{ color: '#666666', fontSize: '14px', margin: 0 }}>
                                Purchase credits to create more sessions.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate(`/plans?redirect=${encodeURIComponent(location.pathname)}`)}
                            style={{
                                background: '#7C3AED',
                                color: '#FFFFFF',
                                padding: '12px 24px',
                                borderRadius: '6px',
                                border: 'none',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            View Plans
                        </button>
                    </div>
                    )}

                    {/* Stats Overview */}
                    <div>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: 600,
                            color: '#000000',
                            marginBottom: '20px',
                        }}>
                            Overview
                        </h2>

                        {(accountsLoading || contextLoading) ? (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '60px',
                            }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    border: '3px solid rgba(0, 0, 0, 0.1)',
                                    borderTopColor: '#7C3AED',
                                    borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite',
                                }} />
                            </div>
                        ) : (
                            <div className="stats-grid">
                                {/* Comments Posted */}
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    borderRadius: '12px',
                                    padding: '24px',
                                }}>
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#666666',
                                        marginBottom: '12px',
                                    }}>
                                        Comments Posted
                                    </div>
                                    <div style={{
                                        fontSize: '36px',
                                        fontWeight: 700,
                                        color: '#000000',
                                        lineHeight: 1,
                                        marginBottom: '8px',
                                    }}>
                                        {stats.commentsPosted}
                                    </div>
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#10B981',
                                        fontWeight: 500,
                                    }}>
                                        Successfully delivered
                                    </div>
                                </div>

                                {/* Comments Generated */}
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    borderRadius: '12px',
                                    padding: '24px',
                                }}>
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#666666',
                                        marginBottom: '12px',
                                    }}>
                                        Comments Generated
                                    </div>
                                    <div style={{
                                        fontSize: '36px',
                                        fontWeight: 700,
                                        color: '#000000',
                                        lineHeight: 1,
                                        marginBottom: '8px',
                                    }}>
                                        {stats.commentsGenerated}
                                    </div>
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#7C3AED',
                                        fontWeight: 500,
                                    }}>
                                        From recent sessions
                                    </div>
                                </div>

                                {/* Pending in Queue */}
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    borderRadius: '12px',
                                    padding: '24px',
                                }}>
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#666666',
                                        marginBottom: '12px',
                                    }}>
                                        Pending in Queue
                                    </div>
                                    <div style={{
                                        fontSize: '36px',
                                        fontWeight: 700,
                                        color: '#000000',
                                        lineHeight: 1,
                                        marginBottom: '8px',
                                    }}>
                                        {stats.pendingInQueue}
                                    </div>
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#F59E0B',
                                        fontWeight: 500,
                                    }}>
                                        Awaiting delivery
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    input::placeholder {
                        color: rgba(0, 0, 0, 0.4);
                    }
                    .stats-grid {
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: 16px;
                    }
                    @media (max-width: 768px) {
                        .stats-grid {
                            grid-template-columns: repeat(2, 1fr);
                        }
                    }
                    @media (max-width: 480px) {
                        .stats-grid {
                            grid-template-columns: 1fr;
                        }
                    }
                `}
            </style>

            {/* Login Modal for existing account authentication */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => {
                    setShowLoginModal(false);
                    setPendingAccountUrl(null);
                    setPendingHashtags(undefined);
                }}
                onSuccess={handleLoginSuccess}
                title="Sign In Required"
                message={`There is already a session existing for the Instagram account @${pendingAccountUrl ? extractUsernameFromUrl(pendingAccountUrl) : ''}. Please sign in to continue.`}
            />
        </>
    );
};

export default SessionsListPage;
